import { AsyncPipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatChip, MatChipSet } from '@angular/material/chips'
import { MatIcon } from '@angular/material/icon'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  ElderDataToolbarComponent,
  ElderTableComponent,
  ElderTableToolbarDirective,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { map } from 'rxjs/operators'
import { GroupedEdge } from '../grouped-edge'
import { GroupedEdgeTarget } from '../grouped-edge-target'
import { ShopfloorEditService } from '../shopfloor-edit.service'
import { ShopfloorLayoutContext } from '../shopfloor-layout-context'
import { ShopfloorLayoutSpec } from '../spec/shopfloor-layout-spec'

@Component({
  selector: 'app-shopfloor-layout-editor',
  templateUrl: './shopfloor-layout-editor.component.html',
  styleUrls: ['./shopfloor-layout-editor.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    MatChipSet,
    MatChip,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ShopfloorLayoutEditorComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  public readonly spec$ = new BehaviorSubject<ShopfloorLayoutSpec>(ShopfloorLayoutSpec.EMPTY)
  public readonly context$ = new BehaviorSubject<ShopfloorLayoutContext>(
    ShopfloorLayoutContext.EMPTY
  )
  public readonly groupedEdges$ = new BehaviorSubject<GroupedEdge[]>([])

  @Output()
  public specChanged = new EventEmitter<ShopfloorLayoutSpec>()

  private editService = inject(ShopfloorEditService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    combineLatest([this.spec$, this.context$])
      .pipe(
        takeUntilDestroyed(),
        map(([spec, context]) => this.editService.buildGroupedEdges(spec, context))
      )
      .subscribe((ges) => this.groupedEdges$.next(ges))
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set spec(spec: ShopfloorLayoutSpec) {
    this.spec$.next(spec)
  }

  @Input()
  public set context(context: ShopfloorLayoutContext) {
    this.context$.next(context)
  }

  @Input()
  public set groupedEdges(groupedEdges: GroupedEdge[]) {
    this.groupedEdges$.next(groupedEdges)
  }

  public get spec(): ShopfloorLayoutSpec {
    return this.spec$.getValue()
  }

  public get context(): ShopfloorLayoutContext {
    return this.context$.getValue()
  }

  public get groupedEdges(): GroupedEdge[] {
    return this.groupedEdges$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openDetailDialog(groupedEdge: GroupedEdge): void {
    this.openDialog(groupedEdge)
  }

  public openCreateDialog() {
    this.openDialog(new GroupedEdge(null, []))
  }

  getLabels(target: GroupedEdgeTarget): string {
    return `${target?.node?.workCenter?.number} ${target?.node?.workCenter?.name}`
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private openDialog(selected?: GroupedEdge) {
    this.editService
      .openNodeEditDialog(
        this.context,
        this.spec.id,
        this.spec.facility,
        this.groupedEdges,
        selected
      )
      .subscribe((newSpec: ShopfloorLayoutSpec) => {
        this.specChanged.emit(newSpec)
      })
  }
}
